import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Description as CsvIcon,
  PictureAsPdf as PdfIcon,
  Code as JsonIcon,
  Archive as ZipIcon,
  Image as ImageIcon,
  TextSnippet as TextIcon,
  Article as DocIcon,
  Download as DownloadIcon,
  Folder as FolderIcon,
  Search as SearchIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { formatFileSize, formatFileDate, getFileIcon } from '../utils/sampleResponses';

/**
 * FileSearchResults - Renders file search results in chat
 * Displays matching files with preview and download capability
 */
const FileSearchResults = ({ fileSearchResults, colors, onDownload, onPreview }) => {
  const { query, results, totalCount } = fileSearchResults;

  // Check if file type supports preview
  const canPreview = (mimeType, fileName) => {
    if (!mimeType && !fileName) return false;
    const type = mimeType || '';
    const name = fileName || '';
    return (
      type === 'text/csv' || name.endsWith('.csv') ||
      type === 'application/json' || name.endsWith('.json') ||
      type.startsWith('text/') ||
      type.startsWith('image/') ||
      type === 'application/pdf'
    );
  };

  // Get file type icon component based on MIME type
  const getFileIconComponent = (mimeType) => {
    const iconType = getFileIcon(mimeType);
    const iconMap = {
      csv: <CsvIcon sx={{ color: '#22c55e' }} />,
      pdf: <PdfIcon sx={{ color: '#ef4444' }} />,
      json: <JsonIcon sx={{ color: '#f59e0b' }} />,
      zip: <ZipIcon sx={{ color: '#8b5cf6' }} />,
      image: <ImageIcon sx={{ color: '#3b82f6' }} />,
      text: <TextIcon sx={{ color: '#71717a' }} />,
      doc: <DocIcon sx={{ color: '#3b82f6' }} />,
      file: <FileIcon sx={{ color: '#71717a' }} />,
    };
    return iconMap[iconType] || iconMap.file;
  };

  // Handle file download
  const handleDownload = (file) => {
    if (onDownload) {
      onDownload({
        id: file.s3Key,
        name: file.name,
        downloadUrl: `/api/files/${encodeURIComponent(file.s3Key)}/download`,
        mimeType: file.mimeType,
        size: file.size,
      });
    }
  };

  // Handle file preview
  const handlePreview = (file) => {
    if (onPreview) {
      onPreview({
        id: file.s3Key,
        name: file.name,
        path: file.path,
        previewUrl: `/api/files/${encodeURIComponent(file.s3Key)}/preview`,
        downloadUrl: `/api/files/${encodeURIComponent(file.s3Key)}/download`,
        mimeType: file.mimeType,
        size: file.size,
      });
    }
  };

  // No results state
  if (!results || results.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 3,
        color: colors.textMuted,
      }}>
        <SearchIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
        <Typography variant="body2">
          No files found matching "{query}"
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {/* Results header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1,
        pb: 1,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          {totalCount} file{totalCount !== 1 ? 's' : ''} found
        </Typography>
        <Chip
          label={query}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            bgcolor: colors.surfaceElevated,
            color: colors.textSecondary,
            borderRadius: 0,
          }}
        />
      </Box>

      {/* Results list */}
      <List sx={{ py: 0 }}>
        {results.map((file, index) => (
          <ListItem
            key={file.s3Key}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 0,
              border: `1px solid ${colors.border}`,
              borderTop: index === 0 ? `1px solid ${colors.border}` : 'none',
              bgcolor: colors.surface,
              '&:hover': {
                bgcolor: colors.hoverBg,
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {getFileIconComponent(file.mimeType)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textPrimary,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name}
                  </Typography>
                </Box>
              }
              secondary={
                <Box sx={{ mt: 0.5 }}>
                  {/* Path */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <FolderIcon sx={{ fontSize: 12, color: '#f59e0b' }} />
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      {file.path}
                    </Typography>
                  </Box>
                  {/* Size and date */}
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    {formatFileSize(file.size)} • {formatFileDate(file.lastModified)}
                  </Typography>
                  {/* Match context if available */}
                  {file.matchContext && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: colors.textMuted,
                        fontStyle: 'italic',
                        mt: 0.5,
                      }}
                    >
                      {file.matchContext}
                    </Typography>
                  )}
                </Box>
              }
            />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canPreview(file.mimeType, file.name) && onPreview && (
                <Tooltip title="Preview">
                  <IconButton
                    size="small"
                    onClick={() => handlePreview(file)}
                    sx={{
                      color: colors.textMuted,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 0,
                      '&:hover': {
                        color: '#3b82f6',
                        borderColor: '#3b82f6',
                        bgcolor: 'rgba(59, 130, 246, 0.08)',
                      },
                    }}
                  >
                    <PreviewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  onClick={() => handleDownload(file)}
                  sx={{
                    color: colors.textMuted,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 0,
                    '&:hover': {
                      color: '#22c55e',
                      borderColor: '#22c55e',
                      bgcolor: 'rgba(34, 197, 94, 0.08)',
                    },
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </ListItem>
        ))}
      </List>

      {/* Footer hint */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 1,
          color: colors.textMuted,
          textAlign: 'center',
        }}
      >
        Click download to save a file, or ask me for more details about any file
      </Typography>
    </Box>
  );
};

export default FileSearchResults;
